import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, AutoComplete, Spin, Select, Upload } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { updateProjectType, getProjectTypeGroups, getProjectTypeById } from '../../../api/project_type';
import { ASSET_BASE_URL } from '../../../api/axios';
import './edit.css';

const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

const isSvgFile = (file: File) =>
  file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');

const EditProjectTypeComponent: React.FC = () => {
  const { id } = useParams<{ id }>();
  const [loading, setLoading] = useState(false);
  const [projectGroups, setProjectGroups] = useState<any[]>([]);
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProjectGroups() {
      setLoading(true);
      try {
        const groups = await getProjectTypeGroups();
        const group = await getProjectTypeById(id);
        setLoading(false);
        setProjectGroups(groups.response);
        setSearchOptions(groups.response.map(group => ({ value: group.display_name, key: group.id })));
        setCurrentLogo(group.response.logo ?? null);
        form.setFieldsValue({ ...group.response, project_group_id: groups.response.find(groupObj => groupObj.id === group.response.project_group_id)?.display_name });
      } catch (error) {
        setLoading(false);
        console.error('Error fetching project groups:', error);
      }
    }

    fetchProjectGroups();
  }, [id, form]);

  const beforeUploadLogo = (file: File) => {
    if (!isSvgFile(file)) {
      message.error({ content: 'Only image/svg+xml is supported', duration: 4, style: { textAlign: 'right' } });
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      message.error({ content: 'Logo exceeds 1 MB', duration: 4, style: { textAlign: 'right' } });
      return Upload.LIST_IGNORE;
    }
    setLogoFile(file);
    return false;
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const project_group_id = projectGroups.find(projectGroup => projectGroup.display_name === values.project_group_id)?.id;
      const formData = new FormData();
      if (values.display_name) formData.append('name', values.display_name);
      if (project_group_id) formData.append('project_group_id', String(project_group_id));
      (values.acronyms || []).forEach((acronym: string) => formData.append('acronyms[]', acronym));
      if (logoFile) formData.append('logo', logoFile);
      await updateProjectType(id, formData);
      message.success({
        content: 'Project type updated successfully',
        duration: 4,
        style: { textAlign: 'right' },
      });
      navigate('/project_types');
    } catch (error: any) {
      console.error('Error updating project type:', error);
      message.error({
        content: error.message,
        duration: 4,
        style: { textAlign: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    const filteredOptions = projectGroups.filter(projectGroup =>
      projectGroup.name.toLowerCase().includes(value.toLowerCase())
    ).map(projectGroupObj => ({ value: projectGroupObj.name, key: projectGroupObj.id }));
    setSearchOptions(filteredOptions);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  return (
    <div className="edit-project-type-container">
      <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />} fullscreen />
      <div className="form-container">
        <h1>Update Project Type</h1>
        <Form
          form={form}
          name="updateProjectTypeForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
        >
          <Form.Item
            label="Name"
            name="display_name"
            rules={[{ required: true, message: 'Please input the name of the project type!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Project Group"
            name="project_group_id"
            rules={[{ required: true, message: 'Please input the project group!' }]}
          >
            <AutoComplete
              options={searchOptions}
              placeholder="Search Project Group"
              onSearch={handleSearch}
            />
          </Form.Item>
          <Form.Item
            label="Search Acronyms"
            name="acronyms"
            extra="Add shorthand names, abbreviations, or alternate terms so users can find this project type when searching. E.g., add 'IFM' for 'Improved Forest Management'."
            normalize={(value: string[]) => {
              if (!value) return [];
              const trimmed = value.map(v => v.trim()).filter(v => v.length > 0);
              const seen = new Set<string>();
              return trimmed.filter(v => {
                const lower = v.toLowerCase();
                if (seen.has(lower)) return false;
                seen.add(lower);
                return true;
              });
            }}
          >
            <Select
              mode="tags"
              placeholder="Type an acronym and press Enter (e.g. IFM, ARR)"
              tokenSeparators={[',']}
              open={false}
            />
          </Form.Item>
          <Form.Item label="Logo" extra="SVG only, max 1 MB. Leave blank to keep the current logo.">
            {currentLogo && (
              <div style={{ marginBottom: 8 }}>
                <img src={`${ASSET_BASE_URL}/${currentLogo}`} alt="Current logo" style={{ maxHeight: 48 }} />
              </div>
            )}
            <Upload
              accept=".svg,image/svg+xml"
              maxCount={1}
              fileList={logoFileList}
              onChange={({ fileList }) => setLogoFileList(fileList)}
              beforeUpload={beforeUploadLogo}
              onRemove={() => setLogoFile(null)}
            >
              <Button icon={<UploadOutlined />}>Replace SVG</Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default EditProjectTypeComponent;
